import React from 'react';
import { formatCurrency } from '@/lib/fmUtils';

/**
 * Rank badge color classes (gold, silver, bronze, gray).
 */
const RANK_COLORS = {
  1: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: '#1' },
  2: { bg: 'bg-gray-100', text: 'text-gray-600', label: '#2' },
  3: { bg: 'bg-orange-100', text: 'text-orange-700', label: '#3' },
};

/**
 * Get progress bar color based on percentage value.
 * Red < 70, Yellow 70-89, Green >= 90
 */
function getMetricBarColor(value) {
  if (value >= 90) return 'bg-green-500';
  if (value >= 70) return 'bg-yellow-500';
  return 'bg-red-500';
}

/**
 * Metric row: label, percentage, and color-coded progress bar.
 */
function MetricRow({ label, value, isCount = false }) {
  const displayValue = isCount ? value : `${Math.round(value)}%`;
  const barPct = isCount ? 0 : Math.min(value, 100);
  const barColor = getMetricBarColor(isCount ? 100 : value);

  return (
    <div className="flex items-center gap-3">
      <span className="text-xs text-slate-600 w-[110px] flex-shrink-0 truncate">{label}</span>
      <span className="text-xs font-semibold text-slate-800 w-[36px] text-right flex-shrink-0">
        {displayValue}
      </span>
      {!isCount && (
        <div className="flex-1 h-1.5 rounded-full bg-slate-200 overflow-hidden">
          <div
            className={`h-1.5 rounded-full transition-all duration-500 ${barColor}`}
            style={{ width: `${barPct}%` }}
          />
        </div>
      )}
    </div>
  );
}

/**
 * TrainerCard - Displays a single trainer's scorecard with rank badge,
 * overall score, 6 metric bars, and revenue figures.
 *
 * Props:
 *   trainer - object from scorecards API
 *   rank    - display rank (1-based)
 *   onClick - tap handler for drill-down
 */
export default function TrainerCard({ trainer, rank, onClick }) {
  const {
    trainer_name = 'Unknown',
    overall_score = 0,
    pt_achievement_pct = 0,
    compliance_7day_pct = 0,
    compliance_14day_pct = 0,
    compliance_30day_pct = 0,
    discipline_pct = 0,
    referrals_count = 0,
    trainer_target = 0,
    trainer_revenue = 0,
  } = trainer || {};

  const rankStyle = RANK_COLORS[rank] || { bg: 'bg-slate-100', text: 'text-slate-500', label: `#${rank}` };
  const scoreColor = overall_score >= 90 ? 'text-green-600' : overall_score >= 70 ? 'text-yellow-600' : 'text-red-600';
  const scoreBarColor = getMetricBarColor(overall_score);

  return (
    <button
      onClick={onClick}
      className="w-full text-left rounded-xl border p-5 bg-white shadow-sm active:scale-[0.98] transition-transform"
    >
      {/* Header: rank badge, name, overall score */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <span className={`px-2 py-0.5 rounded-md text-xs font-bold ${rankStyle.bg} ${rankStyle.text}`}>
            {rankStyle.label}
          </span>
          <span className="text-base font-semibold text-slate-800 truncate">
            {trainer_name}
          </span>
        </div>
        <span className={`text-xl font-bold flex-shrink-0 ${scoreColor}`}>
          {Math.round(overall_score)}%
        </span>
      </div>

      {/* Overall score bar */}
      <div className="h-2 rounded-full bg-slate-200 overflow-hidden mb-4">
        <div
          className={`h-2 rounded-full transition-all duration-500 ${scoreBarColor}`}
          style={{ width: `${Math.min(overall_score, 100)}%` }}
        />
      </div>

      {/* 6 Metric Rows */}
      <div className="space-y-2">
        <MetricRow label="PT Achievement" value={pt_achievement_pct} />
        <MetricRow label="7-Day Compliance" value={compliance_7day_pct} />
        <MetricRow label="14-Day Compliance" value={compliance_14day_pct} />
        <MetricRow label="30-Day Compliance" value={compliance_30day_pct} />
        <MetricRow label="Discipline" value={discipline_pct} />
        <MetricRow label="Referrals" value={referrals_count} isCount />
      </div>

      {/* Revenue figures */}
      <div className="mt-4 pt-3 border-t flex items-center justify-between text-xs text-slate-500">
        <span>Target: {formatCurrency(trainer_target)}</span>
        <span className="font-medium text-slate-700">Achieved: {formatCurrency(trainer_revenue)}</span>
      </div>
    </button>
  );
}
